/**
 * Zomato + Swiggy order parser. Reads Gmail confirmations. India-only.
 *
 * Used by Almari for occasion-coding (late-night order day -> probable
 * casual wear) and reserved for Hisaab v2 spend categorisation.
 */
import type { FoodOrder, Provenance } from './types.ts';
import { paise } from './types.ts';
import { MOCK_FOOD } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

interface GmailListResp { messages?: ReadonlyArray<{ id: string }>; nextPageToken?: string; }
interface GmailMsgResp {
  id: string;
  internalDate: string;
  payload: {
    headers: ReadonlyArray<{ name: string; value: string }>;
    parts?: ReadonlyArray<unknown>;
    body?: { data?: string };
    mimeType?: string;
  };
}

const QUERIES: ReadonlyArray<{ q: string; merchant: FoodOrder['merchant'] }> = [
  { q: 'from:(noreply@zomato.com OR support@zomato.com) subject:(order confirmation OR your order)', merchant: 'zomato' },
  { q: 'from:(noreply@swiggy.in OR orders@swiggy.in) subject:(order placed OR your order)', merchant: 'swiggy' },
  { q: 'from:(noreply@eatsure.com) subject:(order placed)', merchant: 'eatsure' },
];

export interface FoodOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly since_days?: number;
  readonly max?: number;
}

export async function fetchFoodOrders(opts: FoodOpts): Promise<readonly FoodOrder[]> {
  if (opts.mode === 'mock') return MOCK_FOOD;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const since = opts.since_days ?? 365;
  const max = opts.max ?? 200;
  const out: FoodOrder[] = [];

  for (const def of QUERIES) {
    const q = `${def.q} after:${Math.floor((Date.now() - since * 86_400_000) / 1000)}`;
    const listUrl = new URL('https://gmail.googleapis.com/gmail/v1/users/me/messages');
    listUrl.searchParams.set('q', q);
    listUrl.searchParams.set('maxResults', String(Math.min(50, max)));
    const list = await gmailFetch<GmailListResp>(token, listUrl.toString());
    for (const m of list.messages ?? []) {
      const msg = await gmailFetch<GmailMsgResp>(
        token,
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${m.id}?format=full`,
      );
      const parsed = parse(def.merchant, msg);
      if (parsed) out.push(parsed);
      if (out.length >= max) break;
    }
  }
  return out;
}

function parse(merchant: FoodOrder['merchant'], msg: GmailMsgResp): FoodOrder | null {
  const text = decode(msg);
  if (!text) return null;
  const total = matchNum(text, /(?:Order\s*Total|Total|Grand\s*Total)[:\s]*(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)/i);
  const items_count = matchNum(text, /(\d{1,2})\s*items?/i) ?? 1;
  const restaurant = matchStr(text, /from\s+([A-Z][A-Za-z0-9\s'.&,-]{2,40})/i);
  const placed_at = new Date(Number(msg.internalDate)).toISOString();
  const provenance: Provenance = { connector: 'zomato-swiggy', source_id: msg.id, fetched_at: Date.now() };
  return {
    merchant,
    order_id: matchStr(text, /Order\s*(?:#|ID)?[:\s]*([A-Z0-9-]{6,20})/i) ?? msg.id,
    placed_at,
    ...(restaurant ? { restaurant: restaurant.trim() } : {}),
    total: paise((total ?? 0) * 100),
    items_count,
    delivery_window: window(placed_at),
    provenance,
  };
}

type Window = 'morning' | 'lunch' | 'evening' | 'dinner' | 'late_night';
function window(iso: string): Window {
  const h = new Date(iso).getUTCHours() + 5 + 0.5; // IST approx
  if (h >= 6 && h < 11) return 'morning';
  if (h >= 11 && h < 15) return 'lunch';
  if (h >= 15 && h < 18) return 'evening';
  if (h >= 18 && h < 23) return 'dinner';
  return 'late_night';
}
function matchNum(s: string, re: RegExp): number | null {
  const m = s.match(re);
  return m && m[1] ? Number(m[1].replace(/,/g, '')) : null;
}
function matchStr(s: string, re: RegExp): string | null {
  const m = s.match(re);
  return m && m[1] ? m[1] : null;
}
function decode(msg: GmailMsgResp): string | null {
  const data = msg.payload.body?.data;
  if (data) { try { return atob(data.replace(/-/g, '+').replace(/_/g, '/')); } catch { return null; } }
  return null;
}
